// ============================================================================
// feature-flags.js — single read site for localStorage-backed feature flags
// ----------------------------------------------------------------------------
// Local feature preferences still live here. Product-critical paths that must
// be identical across browser origins are exposed as canonical capability
// helpers and deliberately do not read localStorage.
// ============================================================================

/**
 * Read a localStorage flag with default-ON semantics: returns `true` unless
 * the user has explicitly set the key to the string `'false'`. Use this for
 * features that should ship enabled but retain a kill-switch.
 *
 * @param {string} key — localStorage key
 * @returns {boolean}
 */
export function readFlagDefaultOn(key: string): boolean {
    if (typeof localStorage === 'undefined') return true;
    try {
        return localStorage.getItem(key) !== 'false';
    } catch (_e) {
        return true;
    }
}

/**
 * Read a localStorage flag with default-OFF semantics: returns `true` only
 * when the user has explicitly set the key to the string `'true'`. Use this
 * for opt-in / experimental features.
 *
 * @param {string} key — localStorage key
 * @returns {boolean}
 */
export function readFlagDefaultOff(key: string): boolean {
    if (typeof localStorage === 'undefined') return false;
    try {
        return localStorage.getItem(key) === 'true';
    } catch (_e) {
        return false;
    }
}

/**
 * Intake module (RFQ triage queue). Default ON since 2026-07-28 — the operator
 * asked for the rail row to be permanently visible rather than hidden behind a
 * per-device opt-in that every fresh origin lost.
 *
 * NOTE: the FRONTEND row is now always shown, but the BACKEND still gates intake
 * per tenant via `QM_INTAKE_TENANT_ALLOWLIST` plus the `QM_INTAKE_KILL_SWITCH`
 * emergency stop. A tenant outside the allowlist reaches the page and gets an
 * empty/refused queue rather than a missing menu entry. That is the intended
 * trade: a visible door that says "not for you" beats an invisible door.
 *
 * Set `qm_use_intake_module=false` in localStorage to hide it again.
 */
export function isIntakeModuleEnabled(): boolean {
    return readFlagDefaultOn('qm_use_intake_module');
}

/**
 * Quote Price List (read-only charge-out rate schedule). Default ON since
 * 2026-07-28 by operator request. The page is read-only over the live
 * `business_pricing` rate book, so exposing it cannot corrupt pricing data.
 *
 * Set `qm_use_price_lists=false` in localStorage to hide it again.
 */
export function isQuotePriceListEnabled(): boolean {
    // Superseded by the unified surface: once Pricing carries the price list
    // itself, a second rail row pointing at a read-only copy is the confusion
    // DEC-PE-011 exists to remove. The standalone page stays reachable by
    // route (and by turning the unified surface off) — only the duplicate rail
    // row goes.
    if (isPricingUnifiedSurfaceEnabled()) return false;
    return readFlagDefaultOn('qm_use_price_lists');
}

/**
 * One pricing surface (DEC-PE-011). When ON, the Pricing page carries the
 * charge-out Price List as its own landing view, and the duplicate
 * "QUOTE PRICES" rail row disappears.
 *
 * Default ON since 2026-08-07, on the operator's report that two pricing rows
 * in the rail is the problem. Kept as a flag rather than a hard deletion
 * because it is the rollback: setting `qm_pricing_unified_surface=false` in
 * localStorage restores both rows and the old Cost Model landing immediately,
 * with no deploy. The standalone page and its route are NOT removed — that is
 * the separate retirement step, after a real quoting week on this.
 */
export function isPricingUnifiedSurfaceEnabled(): boolean {
    return readFlagDefaultOn('qm_pricing_unified_surface');
}

/**
 * Server-rendered PDF is a canonical product capability. It is deliberately
 * not localStorage-configurable: a fresh browser and an established browser
 * must expose the same Stage 5 PDF and email-attachment workflow.
 */
export function isPuppeteerPdfEnabled(): boolean {
    return true;
}

/**
 * Route file uploads through the authenticated backend (POST /api/files/direct-upload
 * for ≤20 MB, /api/files/upload-stream for larger) instead of a direct
 * browser→MinIO presigned PUT. Default ON since 2026-06-05 (v5.41.51).
 *
 * Why default-ON: the direct PUT targets the host-published MinIO port (:9010),
 * which is unreachable from any device that isn't the Docker host — a second
 * device, tunnelled access, a locked-down LAN — so every upload errored
 * `net::ERR_CONNECTION_REFUSED` and only succeeded after a noisy fallback (and
 * the >25 MB base64-fallback ceiling could drop large videos entirely). The
 * backend always reaches MinIO over the Docker-internal network, so a
 * backend-routed upload works on every device. Confirmed live on Customer
 * Zero's second device 2026-06-05.
 *
 * Opt back into the direct-PUT fast path (browser→MinIO, one less hop) on a LAN
 * where the browser CAN reach :9010: `localStorage.setItem('qm_force_backend_upload','false')`.
 */
export function isBackendUploadForced(): boolean {
    return readFlagDefaultOn('qm_force_backend_upload');
}

/**
 * Catalogue-first cost generation (Pricing Engine, DEC-PE-009). Default ON
 * (v5.41.76, Peter 2026-06-14 — the rate-book wipe that kept this inert on real
 * quotes is fixed (v5.41.30/31), so catalogue-first + no-markup are now the
 * default for all new quote generations).
 *
 * When ON, the BUSINESS PRICING block that `formatPricingForPrompt` injects into
 * every cost-line generator prompt ({PRICING_REFERENCE} — Stage 2 analysis, the
 * per-item recompute, the Stage 3 WMS method-first prompt, Stage 5 quote-gen, and
 * the assistant) carries a directive instructing the AI to SELECT each material /
 * labour / equipment / subcontractor line from the builder's rate book by EXACT
 * catalogue name + rate (e.g. "Carpenter $88", never an invented "Builder $95"),
 * and to name genuine gaps for pricing instead of inventing a number. This is the
 * "first link" of DEC-PE-009 — see docs/pricing-engine/MATCHING-GAP-FINDING.md.
 *
 * ⚠ MUST be enabled together with `qm_rate_final_no_markup` (Slice 0). The steer
 * alone makes the AI use the real catalogue rate, which the cost engine then marks
 * up — WORSE than today. They are two halves of one behaviour
 * (MATCHING-FIX-PROMPT-DRAFT.md §0). `formatPricingForPrompt` emits a one-time
 * console warning when the steer is on but the markup-passthrough is off.
 *
 * Opt OUT per-device (no UI toggle, mirroring `qm_rate_final_no_markup`):
 *   localStorage.setItem('qm_catalogue_first_costing', 'false')
 *
 * @returns {boolean}
 */
export function isCatalogueFirstCostingEnabled(): boolean {
    return readFlagDefaultOn('qm_catalogue_first_costing');
}

/**
 * Rate-book price passthrough (Pricing Engine Slice 0, DEC-PE-007/008).
 * Default ON (v5.41.76, Peter 2026-06-14 — flipped together with
 * `qm_catalogue_first_costing`; the two are halves of one behaviour).
 *
 * When ON, cost lines whose rate came from the rate book
 * (`rateIsFinalIncGst === true`, stamped by pricing-match-applier) pass
 * through cost-math at their final inc-GST price with NO contingency /
 * overhead / margin / GST re-applied — the schedule rate IS the charge-out
 * price (DEC-PE-007), so Rodney's catalogue rate reaches the client unchanged.
 *
 * Centralised here 2026-07-03. This gate previously lived ONLY inside
 * cost-math-adapter.js while helpers.js's §0 coupling warning read the same
 * key with default-OFF semantics — so every fresh device logged a spurious
 * "over-quote" warning about a mis-pairing that wasn't happening. One reader,
 * one default. cost-math-adapter re-exports this for its existing importers.
 *
 * This is now part of the canonical pricing contract. A stale browser value
 * must not change the price of a persisted Project.
 */
export function isRateFinalNoMarkupEnabled(): boolean {
    return true;
}

/**
 * Rate-book read-only during costing (DEC-PE-011 Slice A). Default ON as of
 * v5.41.121 (Peter 2026-06-17: "Rodney is the pricing engine; the AI proposes,
 * never auto-writes"). CODEX-cleared for single-operator Rodney; the only open
 * caveat was multi-operator concurrency, which does not apply to Customer Zero.
 *
 * When ON, a Stage-3 cost recompute stops WRITING the rate book
 * (`business_pricing`): AI-discovered rates are POSTed to the review queue
 * (`pricing_proposals`) instead of being inserted into the catalogue, and usage
 * counts go to the targeted `pricing_usage` counter instead of re-saving the
 * whole catalogue. Net: a recompute makes zero writes to `business_pricing` —
 * the architectural fix beneath DEC-PE-010 (the wipe can't originate from
 * costing because costing no longer writes the book). New rates enter the book
 * only via the Pricing Console or an operator-accepted proposal.
 *
 * Opt-OUT (re-enable auto-write): localStorage.setItem('qm_pricing_readonly_recompute', 'false')
 * See docs/pricing-engine/RATE-BOOK-BOUNDARIES.md.
 *
 * @returns {boolean}
 */
export function isPricingReadonlyRecomputeEnabled(): boolean {
    return readFlagDefaultOn('qm_pricing_readonly_recompute');
}

/**
 * Pricing Worktable — the manual pricing interstitial between Stage 4 (Scope)
 * and Stage 5 (Final Quote). Default OFF (opt-in).
 *
 * When ON, clicking NEXT on Stage 4 opens the Pricing Worktable (a named
 * sub-view, mirroring the Stage 1.5 Project Summary — NO new stage number)
 * instead of going straight to the final quote. The worktable lets the builder
 * price the job by hand — labour (daily/hourly, quoted rate vs cost rate),
 * materials, equipment, admin/site ($660 min) — all in customer-quoted inc-GST
 * dollars, with live margin. It is ADVISORY: "Continue to quote" proceeds to
 * the unchanged Stage 5; nothing here mutates scope_items[].amount. Skipped in
 * batch mode (the runner auto-fires to the quote).
 *
 * Enable per-device: localStorage.setItem('qm_use_pricing_worktable', 'true')
 * (also surfaced as a toggle in Settings → Modules).
 *
 * @returns {boolean}
 */
export function isPricingWorktableEnabled(): boolean {
    return readFlagDefaultOff('qm_use_pricing_worktable');
}

/**
 * Unified cost engine (two-engine consolidation, DEC D1–D4, Peter 2026-06-14).
 * Default OFF — staged. The committed engine (wms-cost-recompute.js) already
 * implements D1 (resolved bracket margin), D2 (engine-1 fixed-overhead
 * denominator) and D3 (per-item $10 round-up, no project-level logical
 * rounding). The only committed-side gap is D4: GST-exempt items (HBCF /
 * statutory insurance) must NOT have GST applied. When this flag is ON, the
 * committed recompute applies a 1.0 GST factor (instead of 1.1) to GST-exempt
 * items, matching how cost-math / project-costing already treat them — so a
 * manually-added HBCF scope item is no longer wrongly GST-wrapped.
 *
 * Non-GST-exempt items are byte-identical with the flag off OR on (the factor
 * stays 1.1), so flipping this moves ONLY GST-exempt items — the staged D4
 * rollout Peter approved. The remaining unification (reconciling the Stage-4
 * DISPLAY engine, cost-math, to the committed engine on D1/D2/D3) is a separate
 * slice that also reads this flag. See docs/pricing-engine/TWO-ENGINE-PARITY-FINDING.md.
 *
 * Default ON as of v5.41.118 — HBCF GST-exemption is now the standard behaviour
 * (Peter 2026-06-17: "HBCF shouldn't be charging 10%"). The flag's only runtime
 * consumer is wms-cost-recompute.js:98; flipping it moves ONLY gst-exempt / HBCF
 * items to a 1.0 GST factor — every other item stays 1.1 (verified minimal blast
 * radius). Opt-OUT per-device: localStorage.setItem('qm_unified_cost_engine', 'false')
 * @returns {boolean}
 */
export function isUnifiedCostEngineEnabled(): boolean {
    return readFlagDefaultOn('qm_unified_cost_engine');
}

/**
 * Fresh-WMS auto scope-consolidation (gutter over-split fix, 2026-06-15).
 * Default OFF — TEMPORARILY REVERTED (v5.41.82, 2026-06-15). Was flipped
 * default-ON in v5.41.81, but the consolidated item came out MIS-PRICED on a
 * live quote ($421.79 vs the correct ~$8,870): the cost did not carry across
 * the morph. Reverted to default-off (correct-priced over-split) until the
 * consolidation cost path is fixed + verified. The consolidation still works
 * via the Stage-4 "Refresh from Work Method" button / opt-in flag, but BOTH
 * share the same morph cost path — treat the consolidated PRICE as suspect
 * until the fix lands. Re-enable per-device to test: set the key to 'true'.
 *
 * The split pipeline derives scope items 1:1 from WMS activities (one item per
 * method step), so a single deliverable like "replace the 4 downpipes" comes
 * out as 5 separately-priced step items instead of one item with the steps as
 * inclusions. RJ Bird's sent quotes are one-item-per-deliverable (median ~2
 * items/quote). When this flag is ON, a FRESH WMS generation re-runs the same
 * proven consolidation the Stage-4 "Refresh from Work Method" button uses
 * (scope-morph) over the just-generated scope — but ONLY when that scope is
 * fresh generator output (>1 item, none locked or user-edited; see
 * `shouldAutoConsolidateScope`), so it never restructures a scope the builder
 * has curated (the v5.12.10 resurrection class). Awaited after the authoritative
 * writeback, not the old racing fire-and-forget.
 *
 * Cost: one extra AI call (the morph) on fresh quote generation when ON.
 *
 * DEFAULT-ON as of v5.41.84 (2026-06-15): the cost path is fixed (QW-FIX-5 —
 * handleScopeMorph re-runs recomputeWmsScopeCost after the morph) and was
 * live-validated on a fresh RJ Bird gutter quote, which consolidated to ONE
 * coherently-priced deliverable matching the sent-quote structure. Per-device
 * opt-OUT: localStorage.setItem('qm_auto_scope_consolidate', 'false')
 * @returns {boolean}
 */
export function isAutoScopeConsolidateEnabled(): boolean {
    return readFlagDefaultOn('qm_auto_scope_consolidate');
}

/**
 * Stage-4 committed-amount authority (Council DEC, QW-FIX-6, 2026-06-15). Closes the
 * Stage-4-vs-Stage-5 cost divergence WITHOUT recompute-on-reload (which would
 * re-price already-sent quotes at current rates). On reload, syncScopeFromActivities
 * re-mirrors each item's costBreakdown from WMS activities, losing the rate-book
 * passthrough stamps, so Stage 4's cost-math display re-derives a marked-up total
 * (e.g. $14k) and its passive amount-derive effect overwrites the committed
 * item.amount with that artifact — while Stage 5 still reads the committed amount
 * (~$8.5k). When ON: (1) the Stage-4 amount-derive effect does NOT overwrite an item
 * carrying amountAuthority==='committed' (recompute output, set in wms-cost-recompute.js);
 * (2) the Stage-4 displayed totals (per-item, top-bar trio, COSTS panel) fall back to
 * the committed item.amount — like Stage 5 — when cost-math diverges from it
 * (scopeDisplayAmountsMatch). Net: Stage 4 == Stage 5; committed amounts are never
 * silently re-priced. No recompute, no async, no race. Per-device kill switch:
 * localStorage.setItem('qm_stage4_amount_authority','false').
 * @returns {boolean}
 */
export function isStage4AmountAuthorityEnabled(): boolean {
    return readFlagDefaultOn('qm_stage4_amount_authority');
}

/** Stage 4 Scope register (Lucid 008 A). Default ON.
 *  Roll back per device: localStorage.setItem('qm_stage4_scope_register', 'false'), then reload. */
export function isStage4ScopeRegisterEnabled(): boolean {
    return readFlagDefaultOn('qm_stage4_scope_register');
}

/**
 * Canonical quote outcome on `projects.quote_status` (CRM data-integrity fix,
 * 2026-06-22). Default ON with kill-switch.
 *
 * Background: `projects` has TWO won/lost columns written by different code.
 * The Stage 5 "Mark Won/Lost" buttons write `projects.status='won'/'lost'`
 * (a pipeline-stage column) but never touch `projects.quote_status` (the
 * outcome column, default 'pending'), while AllQuotesPage writes ONLY
 * `quote_status` via PATCH /projects/:id/quote-status. The whole backend
 * (v_company_analytics, v_property_analytics, companies/properties stats,
 * gold-standards exemplar filter) reads `quote_status`, so a Stage-5 win was
 * invisible to every analytic and the headline win-rate was understated.
 *
 * When ON, the Stage 5 mark-paths ALSO write the canonical `quote_status`
 * (dual-write — `status` is kept for back-compat). The shared reader
 * `getQuoteOutcome` (src/services/quote-outcome.js) recognises BOTH columns
 * unconditionally (it is additive — it can only add win recognition), so the
 * flag's job is to gate the new backend WRITE. Flip OFF to revert Stage 5 to
 * status-only: localStorage.setItem('qm_canonical_quote_status','false').
 *
 * @returns {boolean}
 */
export function isCanonicalQuoteStatusEnabled(): boolean {
    return readFlagDefaultOn('qm_canonical_quote_status');
}

/**
 * Model routing "lanes" view on the AI Models page (2026-06-24). Default ON
 * since v5.46.88. The AI Models page shows a lanes section that groups the
 * ~50 per-task routes into a handful of semantic lanes (intake, vision,
 * reasoning/quote, assistant, learning, deep diagnosis), each with a single
 * model choice that bulk-assigns to all the lane's tasks via setTaskModelOverride.
 * Purely a presentation + bulk-assign layer over the SAME registry — the classic
 * per-task table stays below it, and routing behaviour is unchanged. Toggleable
 * in-page; an explicit `false` remains the per-device escape hatch.
 *
 * Disable per-device: localStorage.setItem('qm_use_model_lanes', 'false')
 * @returns {boolean}
 */
export function isModelLanesEnabled(): boolean {
    return readFlagDefaultOn('qm_use_model_lanes');
}

/**
 * In-app AI-assisted model review on the AI Models page (Part 2 of the model
 * recommendation refresh, 2026-06-25). Default OFF (opt-in).
 *
 * When ON, the AI Models page shows a "Review with AI" panel: an AI model
 * (routed through the 'model-advisor' registry slug → Opus 4.8) reviews the
 * model picked for each protected function, reasoning over the LIVE OpenRouter
 * catalogue (not its training memory), and proposes keep-or-switch per task with
 * a short rationale. The operator accepts per task; accepted picks are written
 * as runtime per-task overrides via setTaskModelOverride — never code
 * recommendations. Non-destructive: it only proposes; nothing changes until the
 * operator clicks Apply. Toggleable in-page so the operator can try it without
 * editing localStorage by hand.
 *
 * Enable per-device: localStorage.setItem('qm_ai_model_advisor', 'true')
 * @returns {boolean}
 */
export function isModelAdvisorEnabled(): boolean {
    return readFlagDefaultOff('qm_ai_model_advisor');
}

/**
 * Stage 3 stale-Work-Method hard-block mode. With the same v5.46.90 wave, a
 * Work Method whose scope_hash no longer matches the live scope reads "stale"
 * and the ONLY exit offered was a full Method regeneration — which rebuilds
 * scope + pricing (destructive) and re-trips the gate after routine Stage 4
 * pricing edits. Default OFF (soft): a method that is complete but merely
 * stale may proceed via an explicit "Continue with current Method" choice;
 * missing/fallback/incomplete methods still block exactly as before.
 *
 * Restore the hard stop per-device:
 * localStorage.setItem('qm_stage3_stale_hard', 'true')
 * @returns {boolean}
 */
export function isStage3StaleHardBlock(): boolean {
    return readFlagDefaultOff('qm_stage3_stale_hard');
}

/**
 * QM Assistant Jobs tools (revise_quote, split_quote, list_quote_revisions,
 * compare_quote_revision, list_job_quotes, open_job_quote). Default OFF per
 * DEC-AU-03 (docs/assistant-upgrade/DECISION-LOG.md): a new Assistant
 * capability stays off until a live check on app.quotemaker.com.au passes,
 * then its default changes to ON. The switch is the 'Assistant Jobs tools'
 * row in Settings, Optional modules (components/settings/registry.ts, opt-in),
 * which must change together with this reader.
 *
 * Switch on per-device: Settings, Optional modules, or
 * localStorage.setItem('qm_assistant_job_tools', 'true')
 * @returns {boolean}
 */
export function isAssistantJobToolsEnabled(): boolean {
    return readFlagDefaultOff('qm_assistant_job_tools');
}
